import type { ApiError } from "@/types/api";

export type ErrorType = "network" | "server" | "auth" | "notFound" | "unknown";

export interface ErrorInfo {
  type: ErrorType;
  message: string;
  code?: number;
}

// 全局错误处理器回调
let errorHandler: ((error: ErrorInfo) => void) | null = null;

/**
 * 设置全局错误处理器
 */
export function setGlobalErrorHandler(
  handler: (error: ErrorInfo) => void,
): void {
  errorHandler = handler;
}

/**
 * 获取错误类型和友好提示信息
 */
export function getErrorInfo(apiError: ApiError): ErrorInfo {
  const code = apiError.code;

  // 网络错误（无响应）
  if (!code) {
    return {
      type: "network",
      message: apiError.message || "无法连接到服务器，请检查网络连接",
    };
  }

  // 根据 HTTP 状态码分类
  switch (code) {
    case 401:
      return {
        type: "auth",
        message: apiError.message || "登录已过期，请重新登录",
        code,
      };
    case 403:
      return {
        type: "auth",
        message: apiError.message || "没有权限执行此操作",
        code,
      };
    case 404:
      return {
        type: "notFound",
        message: apiError.message || "请求的资源不存在",
        code,
      };
    case 500:
    case 502:
    case 503:
    case 504:
      return {
        type: "server",
        message: apiError.message || "服务器错误，请稍后重试",
        code,
      };
    default:
      return {
        type: "unknown",
        message: apiError.message || "操作失败，请重试",
        code,
      };
  }
}

/**
 * 处理API错误
 * 如果设置了全局处理器，会自动触发通知
 */
export function handleApiError(
  apiError: ApiError,
  silent: boolean = false,
): ErrorInfo {
  const errorInfo = getErrorInfo(apiError);

  // 401 错误特殊处理：自动跳转登录页
  if (errorInfo.type === "auth" && errorInfo.code === 401) {
    localStorage.removeItem("kotori-api-token");
    window.location.href = "/auth";
    return errorInfo;
  }

  // 触发全局错误通知（除非设置为静默模式）
  if (!silent && errorHandler) {
    errorHandler(errorInfo);
  }

  return errorInfo;
}

/**
 * 获取错误提示标题
 */
export function getErrorTitle(type: ErrorType): string {
  switch (type) {
    case "network":
      return "网络连接失败";
    case "server":
      return "服务器错误";
    case "auth":
      return "认证失败";
    case "notFound":
      return "资源不存在";
    default:
      return "操作失败";
  }
}
